import { Component, inject, signal, OnInit } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LucideIconComponent } from '../../../lucide-icon/lucide-icon.component';
import { ModalService } from '../../../../../core/services/modal.service';
import { ButtonComponent } from '../../../../ui/button/button.component';
import { TextComponent } from '../../../../ui/typography/text/text.component';
import { InputComponent } from '../../input/input.component';

import { SelectComponent } from '../../select/select.component';
import { FmcsaService, FmcsaCarrier } from '../../../../../core/services/fmcsa.service';
import { PartnerService } from '../../../../../core/services/partner.service';
import { ToastService } from '../../../../ui/toast/toast.service';
import { CompanySearchResult } from '../../../../../core/services/company-search.service';
import { FormFieldComponent } from '../../form-field/form-field.component';

@Component({
  selector: 'app-dot-search-modal',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    LucideIconComponent,
    ButtonComponent,
    TextComponent,
    InputComponent,
    SelectComponent,
    FormFieldComponent
],
  templateUrl: './dot-search-modal.component.html',
})
export class DotSearchModalComponent implements OnInit {
  private fmcsaService = inject(FmcsaService);
  private partnerService = inject(PartnerService);
  private toastService = inject(ToastService);
  private modalService = inject(ModalService);

  protected readonly onSuccess = this.modalService.state()?.data?.onSuccess;
  protected readonly initialName = this.modalService.state()?.data?.initialName;

  fmcsaSearchType = signal<'dot' | 'mc'>('dot');
  fmcsaSearchQuery = signal('');

  fmcsaTypeOptions = [
    { value: 'dot', label: 'DOT Number' },
    { value: 'mc', label: 'MC/Docket Number' }
  ];

  isSearchingFmcsa = signal(false);
  fmcsaResult = signal<FmcsaCarrier | null>(null);
  fmcsaError = signal<string | null>(null);
  
  partnerType = signal<'broker' | 'customer' | 'carrier'>('broker');
  partnerTypeOptions = [
    { value: 'broker', label: 'Broker' },
    { value: 'customer', label: 'Customer' },
    { value: 'carrier', label: 'Carrier' }
  ];

  ngOnInit() {
    // We could prepopulate the search if initialName looks like a DOT/MC number
    if (this.initialName && /^\d+$/.test(this.initialName.trim())) {
      this.fmcsaSearchQuery.set(this.initialName.trim());
    }
  }

  close() {
    this.modalService.close();
  }

  searchFmcsa() {
    const query = this.fmcsaSearchQuery().trim();
    if (!query) return;

    this.isSearchingFmcsa.set(true);
    this.fmcsaResult.set(null);
    this.fmcsaError.set(null);

    const req$ = this.fmcsaSearchType() === 'dot'
      ? this.fmcsaService.lookupDotNumber(query)
      : this.fmcsaService.lookupDocketNumber(query);

    req$.subscribe({
      next: (res) => {
        this.isSearchingFmcsa.set(false);
        if (res.error) {
          this.fmcsaError.set(res.error);
        } else if (res.carrier) {
          this.fmcsaResult.set(res.carrier);
        }
      },
      error: () => {
        this.isSearchingFmcsa.set(false);
        this.fmcsaError.set('A network error occurred while reaching the FMCSA registry.');
      }
    });
  }

  async addFmcsaPartner() {
    const carrier = this.fmcsaResult();
    if (!carrier) return;

    try {
      const partnerId = await this.partnerService.ensurePartnerExists({
        partnerCompanyId: carrier.dotNumber ? String(carrier.dotNumber) : undefined, // DOT number acts as ID for FMCSA sourced partners initially
        partnerType: this.partnerType(),
        partnerName: carrier.legalName,
        dbaName: carrier.dbaName,
        dotNumber: carrier.dotNumber ? String(carrier.dotNumber) : undefined,
        mcNumber: carrier.mcNumber ? String(carrier.mcNumber) : undefined,
        source: 'dot',
        address: carrier.address,
        physicalAddress: carrier.physicalAddress,
        mailingAddress: carrier.mailingAddress,
        isCarrier: carrier.isCarrier,
        isBroker: carrier.isBroker,
        powerUnits: carrier.powerUnits,
        drivers: carrier.drivers,
        contactPhone: carrier.phoneNumber,
        faxNumber: carrier.faxNumber,
        email: carrier.emailAddress,
      });

      this.toastService.show('success', 'Company added successfully from DOT Registry');
      this.modalService.close();
      
      const result: CompanySearchResult = {
        id: partnerId,
        source: 'dot',
        collection: 'companyRelationships',
        legalName: carrier.legalName,
        dotNumber: carrier.dotNumber ? String(carrier.dotNumber) : undefined,
      };
      
      this.onSuccess?.(result);
    } catch (err: any) {
      this.toastService.show('error', err.message || 'Failed to add company');
    }
  }
}
